<template>
  <v-row
    :class="{
      'px-6 pb-2 pt-5 mx-auto my-0 text-caption': true,
      'lead-gen-styles': isLeadGen,
      [bgColor]: true,
    }"
  >
    <v-col>
      <div v-for="disclosure in disclosuresText" :key="JSON.stringify(disclosure)" class="mb-4">
        <template v-for="text in disclosure.textList" :key="JSON.stringify(text)">
          <strong v-if="disclosure.shouldBeBold">
            <a v-if="text.type === 'anchor'" :href="text.href" class="text-caption" target="_blank"
              ><u>{{ text.text }}</u></a
            >
            <br v-else-if="text.type === 'break'" />
            <span v-else>{{ text.text }} </span>
          </strong>
          <span v-else
            ><a v-if="text.type === 'anchor'" :href="text.href" class="text-caption" target="_blank"
              ><u>{{ text.text }}</u></a
            >
            <br v-else-if="text.type === 'break'" />
            <span v-else>{{ text.text }} </span></span
          >
        </template>
      </div>

      <div v-if="!isLeadGen">
        <a
          v-for="footerLink in footerDisclosureContent"
          :key="footerLink.linkurl"
          :href="!isPreview ? footerLink.linkurl : undefined"
          target="_blank"
          class="mr-5 text-caption"
          >{{ footerLink.linkname }}</a
        >
      </div>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import type { Disclosures, ParsedDisclosure } from '@/interfaces/PegaAgent';
import { parseDisclosureLineSections, removeHREFFromAnchors } from '@/utils/common';
import { Country } from '@/constants/Countries';
import { Translations } from '@/i18n/models';
import { mapState } from 'pinia';
import PegaApiStore from '@/stores/pegaApi.store';

export default defineComponent({
  props: {
    defaultLanguage: {
      type: String,
      required: false,
    },
    isLeadGen: {
      type: Boolean,
    },
    disclosures: {
      type: Array as PropType<Disclosures[]>,
      default: () => [],
      required: true,
    },
    isPreview: {
      type: Boolean,
    },
    bgColor: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      Translations,
      pegaStore: PegaApiStore(),
    };
  },
  computed: {
    ...mapState(PegaApiStore, ['pegaCRMJSON']),
    disclosuresSorted() {
      return [
        ...this.disclosures.filter((dis: Disclosures) => dis.lang.includes(Country.CA)),
        ...this.disclosures.filter((dis: Disclosures) => dis.lang.includes(Country.US)),
      ].filter((dis: Disclosures) => dis.desc.length);
    },
    disclosuresText(): ParsedDisclosure[] {
      if (this.disclosures.length) {
        return this.disclosuresSorted
          .map((dis: Disclosures) => dis.descHtml ?? dis.desc)
          .map((desc: string) =>
            this.parseDisclosureTextEntries(this.isPreview ? removeHREFFromAnchors(desc) : desc)
          );
      }

      return [];
    },
    disclosuresLinkUrl() {
      if (!this.pegaCRMJSON?.agentInfo.country) return '';
      return this.pegaCRMJSON.agentInfo.country === Country.United_States
        ? this.$t(Translations.importantDisclosuresLinkUS)
        : this.$t(Translations.importantDisclosuresLinkCanada);
    },
    termsLinkUrl() {
      if (!this.pegaCRMJSON?.agentInfo.country) return '';
      return this.pegaCRMJSON.agentInfo.country === Country.United_States
        ? this.$t(Translations.termsOfUseLinkUS)
        : this.$t(Translations.termsOfUseLinkCanada);
    },
    privacyPolicyLinkUrl() {
      if (this.pegaCRMJSON?.agentInfo.country) {
        return this.pegaCRMJSON.agentInfo.country === Country.United_States
          ? this.$t(Translations.privacyPolicyLinkUS)
          : this.$t(Translations.privacyPolicyLinkCanada);
      }
      return '';
    },
    footerDisclosureContent() {
      return [
        {
          linkname: this.$t(Translations.importantDisclosures),
          linkurl: this.disclosuresLinkUrl,
        },
        {
          linkname: this.$t(Translations.termsOfUse),
          linkurl: this.termsLinkUrl,
        },
        {
          linkname: this.$t(Translations.privacyPolicy),
          linkurl: this.privacyPolicyLinkUrl,
        },
      ];
    },
  },
  methods: {
    parseDisclosureTextEntries(disclosure: string): ParsedDisclosure {
      const shouldBeBold =
        !!disclosure &&
        (disclosure.includes('Note') ||
          disclosure.includes('Nota') ||
          disclosure.includes('Remarque'));
      return {
        shouldBeBold,
        textList: parseDisclosureLineSections(disclosure),
      };
    },
  },
});
</script>
<style>
.lead-gen-styles {
  background-color: #a5bac5;
  max-width: 900px !important;
}
</style>
